import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { uploadCaseDocument } from "@/services/storage-upload"
import { oiaService } from "@/features/oia/services/oia.service"
import type { PreparedOiaInput, PreparedOioInput, SimulatorService } from "@/features/drafting/simulatorHandoff"
import type { OiaDocumentRef } from "@/features/oia/types"
import type { OiaTemplateSummary } from "@/features/oia/types"
import type { StartDraftInput } from "@/features/oia/hooks/useOiaRuns"
import { OiaIntake } from "@/features/oia/components/intake/OiaIntake"
import { CaseFileUploader, type PreparedOioFiles } from "@/features/file-review/components/CaseFileUploader"
import { useSimulatorSubmit } from "./useSimulatorSubmit"

export function SimulatorModal({ service, onClose }: { service: SimulatorService; onClose: () => void }) {
  const auth = useAuth()
  const userId = auth.session?.user.id ?? ""
  const accountAtOpen = useRef(userId)
  const createAndOpenCase = useSimulatorSubmit(userId, () => userId === accountAtOpen.current && !!auth.session?.user.id)
  const closeRef = useRef<HTMLButtonElement>(null)
  const restoreFocus = useRef<HTMLElement | null>(null)
  const submitting = useRef(false)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(busy)
  busyRef.current = busy
  const uploadedOio = useRef(new Map<string, string>())
  const uploadedOia = useRef(new Map<string, OiaDocumentRef>())
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [templates, setTemplates] = useState<OiaTemplateSummary[] | null>(null)
  const [templatesFailed, setTemplatesFailed] = useState(false)

  useEffect(() => {
    if (service !== "oia") return
    let cancelled = false
    void oiaService.getTemplates().then((result) => {
      if (!cancelled) setTemplates(result.templates ?? [])
    }).catch(() => {
      if (!cancelled) { setTemplates([]); setTemplatesFailed(true) }
    })
    return () => { cancelled = true }
  }, [service, userId])

  useEffect(() => {
    restoreFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    closeRef.current?.focus()
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busyRef.current) onClose()
      if (event.key !== "Tab") return
      const nodes = Array.from(document.querySelectorAll<HTMLElement>("[data-simulator-dialog] button:not(:disabled), [data-simulator-dialog] input:not(:disabled), [data-simulator-dialog] select:not(:disabled), [data-simulator-dialog] textarea:not(:disabled), [data-simulator-dialog] [tabindex='0']"))
      if (!nodes.length) return
      const first = nodes[0], last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener("keydown", handleKey)
    return () => { document.removeEventListener("keydown", handleKey); restoreFocus.current?.focus() }
  }, [onClose])

  const submitOio = async (files: PreparedOioFiles): Promise<void> => {
    if (busy || submitting.current || !userId) return
    submitting.current = true
    setBusy(true); setError(""); setNotice("Checking draft access…")
    try {
      if (userId !== accountAtOpen.current || !auth.session?.user.id) throw new Error("Your account changed. Reopen the simulator to continue.")
      setNotice("Uploading documents…")
      const resolve = async (entry: { name: string; file?: File; url?: string }) => {
        if (entry.url) return entry.url
        if (!entry.file) throw new Error(`Could not read ${entry.name}. Choose it again and retry.`)
        const key = `${entry.file.name}:${entry.file.size}:${entry.file.lastModified}`
        const cached = uploadedOio.current.get(key)
        if (cached) return cached
        const url = await uploadCaseDocument(entry.file, userId)
        uploadedOio.current.set(key, url)
        return url
      }
      const scnUrl = await resolve(files.scn)
      const replyUrl = files.reply ? await resolve(files.reply) : null
      const supporting = []
      for (const entry of files.supporting) supporting.push({ url: await resolve(entry), filename: entry.name })
      const ready: PreparedOioInput = {
        scn: { url: scnUrl, filename: files.scn.name },
        reply: files.reply ? { url: replyUrl as string, filename: files.reply.name } : null,
        supporting,
        exParte: files.exParte,
      }
      const metadata: Record<string, unknown> = {
        scnFileUrl: ready.scn.url, scnFileName: ready.scn.filename,
        replyFileUrl: ready.reply?.url ?? null, replyFileName: ready.reply?.filename ?? null,
        replyStatus: ready.reply ? "received" : "not_received",
        // CaseFileUploader restores the established `name` key after reload;
        // retain it alongside `filename` used by simulator handoffs.
        supportingFiles: ready.supporting.map((file) => ({ name: file.filename, filename: file.filename, url: file.url })),
      }
      if (userId !== accountAtOpen.current || !auth.session?.user.id) throw new Error("Your account changed. Reopen the simulator to continue.")
      setNotice("Preparing your case…")
      await createAndOpenCase("oio", ready, metadata)
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The simulator could not prepare this case. Your files are kept; retry when ready.")
      setNotice("")
      throw caught
    } finally {
      submitting.current = false
      setBusy(false)
    }
  }

  const submitOia = async (input: StartDraftInput): Promise<boolean> => {
    if (busy || submitting.current || !userId) return false
    submitting.current = true; setBusy(true); setError(""); setNotice("Checking draft access…")
    try {
      if (userId !== accountAtOpen.current || !auth.session?.user.id) throw new Error("Your account changed. Reopen the simulator to continue.")
      setNotice("Uploading documents…")
      const documents: OiaDocumentRef[] = [...input.existing]
      for (const upload of input.uploads) {
        const key = `${upload.file.name}:${upload.file.size}:${upload.file.lastModified}`
        let ref = uploadedOia.current.get(key)
        if (!ref) {
          ref = { url: await uploadCaseDocument(upload.file, userId), filename: upload.file.name, role_hint: upload.roleHint }
          uploadedOia.current.set(key, ref)
        }
        documents.push({ ...ref, role_hint: upload.roleHint })
      }
      if (userId !== accountAtOpen.current || !auth.session?.user.id) throw new Error("Your account changed. Reopen the simulator to continue.")
      setNotice("Preparing your case…")
      const prepared: PreparedOiaInput = {
        documents, caseView: input.caseView, templateId: input.templateId,
        ...(input.officeId ? { officeId: input.officeId } : {}), ...(input.officerId ? { officerId: input.officerId } : {}),
      }
      await createAndOpenCase("oia", prepared, { oia_documents: documents, oia_template_id: input.templateId })
      onClose()
      return true
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The simulator could not prepare this case. Your files are kept; retry when ready.")
      setNotice("")
      return false
    } finally { submitting.current = false; setBusy(false) }
  }

  const title = service === "oio" ? "OIO simulator" : "OIA simulator"
  return (
    <div className="simulator-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose() }}>
      <section className="simulator-dialog" role="dialog" aria-modal="true" aria-labelledby="simulator-title" data-simulator-dialog>
        <header className="simulator-dialog__head">
          <div><p className="simulator-eyebrow">Drafting simulator</p><h2 id="simulator-title">{title}</h2></div>
          <button ref={closeRef} type="button" className="simulator-close" onClick={onClose} aria-label="Close simulator" disabled={busy}>×</button>
        </header>
        <div className="simulator-dialog__body">
          {service === "oio" ? <>
            <CaseFileUploader stagedOnly onPrepare={submitOio} />
          </> : <>
            <OiaIntake threadId="" stagedOnly existing={[]} identified={[]} templates={templates} templatesFailed={templatesFailed} busy={busy} initialTemplateId="" initialCaseView="" notice={null} onGenerate={submitOia} />
          </>}
        </div>
        <footer className="simulator-dialog__foot">
          <div aria-live="polite">{error && <p className="simulator-error" role="alert">{error}</p>}{notice && <p className="simulator-notice" role="status">{notice}</p>}</div>
          {service === "oio" && notice && <p className="simulator-notice">{busy ? notice : ""}</p>}
        </footer>
      </section>
    </div>
  )
}

